import type { AnalyticDateFilter } from '~/presentation/ui';

import { EFilterField, EOperatorField } from '~/presentation/reports/common/type';
import { useCampaignMaps } from '~/modules/campaign-template';
import { useReportControlContext } from '../contexts';
import { OPERATOR_IS } from '../constants';
import { useMemo } from 'react';

interface UseGetVersionProps {
  currentDates: AnalyticDateFilter;
}

export const useGetVersion = ({ currentDates }: UseGetVersionProps) => {
  const { filters } = useReportControlContext();
  const { campaignMap, campaignGroupMap } = useCampaignMaps();

  const campaignId =
    filters[EFilterField.SINGLE_CAMPAIGN] ||
    filters[EFilterField.PRICE_CAMPAIGN] ||
    filters[EFilterField.URL_CAMPAIGN];
  const campaignGroupId = filters[EFilterField.GROUP_CAMPAIGN];
  const campaignItem = filters[EFilterField.GROUP_CAMPAIGN_ITEM];
  const campaignItems = filters[EFilterField.GROUP_CAMPAIGN_ITEMS];
  const campaignItemOperator = filters[EOperatorField.CAMPAIGN_ITEM_OPERATOR];

  const campaignIdsInGroup: string[] = useMemo(() => {
    if (!campaignGroupId) return [];

    if (campaignItemOperator === OPERATOR_IS && campaignItem) {
      return [campaignItem];
    }

    return campaignItems ?? [];
  }, [campaignItems, campaignItem, campaignGroupId, campaignItemOperator]);

  const versionsByCampaignId = useMemo(() => {
    return campaignMap[campaignId]?.versions;
  }, [campaignMap, campaignId]);

  const campaignGroup = useMemo(() => {
    return campaignGroupMap[campaignGroupId];
  }, [campaignGroupMap, campaignGroupId]);

  const versionsByGCampaignId = useMemo(() => {
    return campaignGroup?.versions;
  }, [campaignGroup]);

  const campaignVersion = useMemo(() => {
    if (!versionsByCampaignId) return undefined;

    const campaignVersion = versionsByCampaignId.find(
      (version) => version?.version === currentDates.alias,
    );
    return campaignVersion;
  }, [versionsByCampaignId, currentDates]);

  const groupCampaignVersion = useMemo(() => {
    if (!versionsByGCampaignId) return undefined;

    const campaignVersion = versionsByGCampaignId.find(
      (version) => version?.version === currentDates.alias,
    );
    return campaignVersion;
  }, [versionsByGCampaignId, currentDates]);

  const campaignVersionInGroup = useMemo(() => {
    if (!campaignGroup || campaignIdsInGroup.length === 0 || campaignIdsInGroup.length > 1)
      return undefined;

    const campaignId = campaignIdsInGroup[0];

    const campaign = campaignGroup.campaigns?.find((campaign) => campaign.id === campaignId);
    if (!campaign || !campaign.versions) return undefined;

    return campaign.versions.find((version) => version?.version === currentDates.alias);
  }, [campaignIdsInGroup, currentDates, campaignGroup]);

  const versionIds = useMemo(() => {
    if (campaignGroupId && campaignVersionInGroup) {
      return {
        [EFilterField.CAMPAIGN_VERSION_ID]: campaignVersionInGroup.id,
      };
    }

    if (campaignVersion?.id) {
      return {
        [EFilterField.CAMPAIGN_VERSION_ID]: campaignVersion.id,
      };
    }

    if (groupCampaignVersion?.id) {
      return {
        [EFilterField.GROUP_CAMPAIGN_VERSION_ID]: groupCampaignVersion.id,
      };
    }

    return {};
  }, [campaignVersion, groupCampaignVersion, campaignVersionInGroup, campaignGroupId]);

  return {
    versionIds,
  };
};
